import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  Package,
  Settings,
  LogOut,
  User,
  Shield,
  ChevronDown,
  Menu,
  Briefcase,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/jobs", label: "Jobs", icon: FolderOpen },
  { path: "/inbox", label: "Inbox", icon: Mail },
  { path: "/products", label: "Products", icon: Package },
];

const ADMIN_NAV_ITEMS = [
  { path: "/settings", label: "Settings", icon: Settings, adminOnly: true },
];

export default function MainNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Check which mode is active
  const isBusinessQuoteActive = location.pathname.startsWith("/quote");

  const startBusinessQuote = () => {
    navigate("/quote?new=business");
  };

  const allNavItems = [...NAV_ITEMS, ...(isAdmin ? ADMIN_NAV_ITEMS : [])];

  return (
    <header className="h-16 bg-aa-card shadow-[0_2px_8px_rgba(0,0,0,0.06)] sticky top-0 z-40">
      <div className="max-w-aa-container mx-auto px-6 h-full flex items-center justify-between">
        {/* Left: Logo + Navigation */}
        <div className="flex items-center gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <Logo size="sm" showText={true} className="hidden sm:flex" />
            <Logo size="sm" showText={false} className="flex sm:hidden" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {allNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path}>
                  <button
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-aa-md text-aa-sm font-medium
                      transition-all duration-200
                      ${active 
                        ? "bg-aa-bg shadow-aa-inset text-aa-primary" 
                        : "text-gray-600 hover:text-aa-primary hover:bg-aa-bg/50"
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                </Link>
              );
            })}
          </nav>

          {/* Mobile Navigation Menu */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-nav-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 rounded-aa-md shadow-aa-card bg-aa-card">
                {allNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center w-full">
                        <Icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
                {/* New Quote in Mobile Menu */}
                <div className="border-t my-1" />
                <DropdownMenuItem asChild>
                  <Link 
                    to="/quote?new=business" 
                    className={`flex items-center w-full ${isBusinessQuoteActive ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    New Quote
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Right: Workflow Actions + User Menu */}
        <div className="flex items-center gap-3">
          {/* New Quote - Always visible */}
          <button
            onClick={startBusinessQuote}
            data-testid="nav-new-quote"
            className={`
              flex items-center gap-1.5 px-4 py-2.5 rounded-aa-md
              font-medium text-aa-sm transition-all duration-200
              ${isBusinessQuoteActive 
                ? "bg-blue-600 text-white shadow-[0_4px_12px_rgba(30,58,95,0.4)] ring-2 ring-blue-300" 
                : "bg-aa-primary/80 text-white hover:bg-aa-primary hover:shadow-[0_4px_12px_rgba(30,58,95,0.3)] hover:-translate-y-0.5"
              }
            `}
          >
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">New Quote</span>
          </button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="
                  flex items-center gap-2 px-3 py-2 rounded-aa-md
                  bg-aa-card shadow-aa-button text-aa-sm font-medium
                  transition-all duration-200
                  hover:-translate-y-0.5 active:shadow-aa-inset active:translate-y-0
                "
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    isAdmin ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {isAdmin ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <span className="hidden sm:inline">{user?.username}</span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-aa-md shadow-aa-card bg-aa-card p-2">
              <div className="px-3 py-2 border-b border-gray-100 mb-2">
                <p className="font-medium text-aa-sm">{user?.username}</p>
                <p className="text-aa-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <DropdownMenuItem
                onClick={logout}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-aa-sm"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
